import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useV2 } from './ctx';
import { rp, shortDate } from './model';
import { fcLinesFor } from './fcLines';

// The menu on a forecast trip in Buku Kas (Director, 23 Oct: "make it simpler.
// in a drop down", "apple level"). Opens where the row was tapped, like a
// macOS/iOS context menu; drawn on <body> so the ledger's rounded, clipped
// table can't cut it off. Keyboard: ↑ ↓ Home End to move, Enter to pick,
// Esc or Tab to close. Closes on a tap outside, scroll or resize.
const time = at => new Date(at + 7 * 3600e3).toISOString().slice(11, 16).replace(':', '.');
const Ico = ({ d }) => <svg className="fcm-ico" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d={d} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>;
const X = 'M8 14.5a6.5 6.5 0 1 0 0-13 6.5 6.5 0 0 0 0 13zM5.75 5.75l4.5 4.5M10.25 5.75l-4.5 4.5';
const CHECK = 'M8 14.5a6.5 6.5 0 1 0 0-13 6.5 6.5 0 0 0 0 13zM5.2 8.2l1.9 1.9 3.7-3.9';
const BACK = 'M6 4.5 3 7.5l3 3M3.5 7.5H10a3 3 0 0 1 0 6H8';
const PLUS = 'M8 14.5a6.5 6.5 0 1 0 0-13 6.5 6.5 0 0 0 0 13zM8 5.2v5.6M5.2 8h5.6';

// Outside FcMenu on purpose: a component defined inside another is rebuilt on
// every render, which threw away the focused item when the open animation re-rendered.
function Item({ onClick, icon, title, sub, danger, busy }) {
  return (
    <button type="button" role="menuitem" tabIndex={-1} className={`fcm-i${danger ? ' danger' : ''}`} disabled={busy} onClick={onClick}>
      <Ico d={icon} /><span className="fcm-t"><b>{title}</b>{sub && <span>{sub}</span>}</span>
    </button>
  );
}

export function FcMenu({ m, onClose }) {
  const { api, monthKey, done, planId, user, role } = useV2();
  const ref = useRef(null);
  const [pos, setPos] = useState({ left: m.x, top: m.y, origin: 'top left', shown: false });
  const [busy, setBusy] = useState(false);
  const { ev, along } = m, d = ev.dismissed;

  // place it inside the window: flip left / up when it would run off the edge
  useLayoutEffect(() => {
    const el = ref.current; if (!el) return;
    const w = el.offsetWidth, h = el.offsetHeight, pad = 12;
    const right = m.x + w > window.innerWidth - pad, up = m.y + h > window.innerHeight - pad && m.y - h > pad;
    setPos({ left: Math.max(pad, right ? m.x - w : m.x), top: up ? m.y - h : Math.min(m.y, window.innerHeight - h - pad), origin: `${up ? 'bottom' : 'top'} ${right ? 'right' : 'left'}`, shown: false });
    // opened by keyboard: first item lit, like macOS; by mouse or touch: nothing lit until hovered
    const first = el.querySelector('[role="menuitem"]');
    if (m.kb && first) first.focus({ preventScroll: true }); else el.focus({ preventScroll: true });
    // one painted frame at scale .96 first, so the open animation actually plays
    const raf = requestAnimationFrame(() => setPos(p => ({ ...p, shown: true })));
    return () => cancelAnimationFrame(raf);
  }, [m]);

  useEffect(() => {
    const items = () => [...(ref.current?.querySelectorAll('[role="menuitem"]:not([disabled])') || [])];
    // a tap on the trip that opened it is left to its own click, which closes (toggle)
    const onDown = e => { if (ref.current && !ref.current.contains(e.target) && !(m.opener && m.opener.contains(e.target))) onClose(); };
    const onKey = e => {
      if (e.key === 'Escape' || e.key === 'Tab') { e.preventDefault(); onClose(true); return; }
      const list = items(), i = list.indexOf(document.activeElement);
      const go = j => { e.preventDefault(); list[(j + list.length) % list.length]?.focus(); };
      if (e.key === 'ArrowDown') go(i + 1); else if (e.key === 'ArrowUp') go(i - 1); else if (e.key === 'Home') go(0); else if (e.key === 'End') go(list.length - 1);
    };
    const onScroll = e => { if (!ref.current?.contains(e.target)) onClose(); };
    const onResize = () => onClose();
    window.addEventListener('pointerdown', onDown, true); window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true); window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('pointerdown', onDown, true); window.removeEventListener('keydown', onKey); window.removeEventListener('scroll', onScroll, true); window.removeEventListener('resize', onResize); };
  }, [onClose, m.opener]);

  const act = async (f, msg) => { setBusy(true); try { await f(); onClose(true); await done(msg); } catch (e) { setBusy(false); alert(e.message); } };
  const reject = reason => act(() => api.dismissForecast(monthKey, ev, reason), reason === 'tidak_jalan' ? `${ev.label} tidak dihitung lagi` : `${ev.label}: sudah ada di plan`);
  // take the trip into the plan as the lines Aurel would type (fcLines.js); a lapak return brings its receipt
  const take = () => act(async () => {
    for (const v of fcLinesFor(ev, [ev, ...along])) await api.createAmendment({ planId, planLineId: null, monthKey, amendmentType: v.penerimaan > 0 ? 'add_inflow' : 'add_disbursement', originalValue: null, reason: 'Dari perkiraan (posisi truk)', requestedBy: user.name, requestedValue: JSON.stringify({ ...v, rekening: 'BCA' }) });
  }, `${ev.label} masuk plan${role === 'director' ? '' : ' · menunggu Direktur'}`);
  const undo = () => act(() => api.undoForecastDismiss(d.id), `${ev.label} kembali ke perkiraan`);

  return createPortal(
    <div ref={ref} tabIndex={-1} className={`fcm${pos.shown ? ' in' : ''}`} role="menu" aria-label={`Perkiraan ${ev.label}`} style={{ left: pos.left, top: pos.top, transformOrigin: pos.origin }}>
      <div className="fcm-h"><b>{ev.label}</b><span>{shortDate(ev.date)} ±{time(ev.at)} · {rp(Math.abs(ev.amount))}</span></div>
      <div className="fcm-sep" />
      {d ? <>
        <div className="fcm-note">Ditandai {d.reason === 'di_plan' ? 'sudah ada di plan' : 'tidak jalan'}{d.by ? ` oleh ${d.by.split(' ')[0]}` : ''}</div>
        <Item busy={busy} onClick={undo} icon={BACK} title="Kembalikan ke perkiraan" />
      </> : <>
        <Item busy={busy} onClick={take} icon={PLUS} title="Masukkan ke plan" sub={along.some(x => x.kind === 'recv') ? 'Dengan terima lapak · koordinasi tidak' : 'Sebagai baris baru'} />
        <Item busy={busy} onClick={() => reject('tidak_jalan')} icon={X} title="Tidak akan jalan" sub="Keluar dari saldo dan Ritase" danger />
        <Item busy={busy} onClick={() => reject('di_plan')} icon={CHECK} title="Sudah ada di plan" sub="Keluar dari saldo saja" />
        {along.length > 0 && <><div className="fcm-sep" /><div className="fcm-note">{along.map(x => (x.kind === 'koord' ? 'Koordinasi' : 'Terima lapak')).join(' dan ')} hari itu ikut</div></>}
      </>}
    </div>,
    document.body
  );
}
